import { withdrawConsentSchema } from "@closer/shared";
import { Button, EmptyState, PageHeader, Spinner, Table } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";

import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { resolveErrorState } from "../../lib/uiState";

/**
 * Onay geçmişi ve izin yönetimi (CLOS-361).
 *
 * Kullanıcı burada **hangi metnin hangi sürümünü ne zaman kabul ettiğini** görür. Sunucu
 * teknik özeti ve iç kimlikleri zaten göndermiyor; ekran da onları uydurmaz.
 */
export type ConsentHistoryRow = {
  documentId: string;
  type: string;
  version: number;
  effectiveFrom: string;
  required: boolean;
  decision: string;
  source: string;
  grantedAt: string;
  current: boolean;
};

const BASLIKLAR: Record<string, string> = {
  terms_of_service: "Kullanım Şartları",
  privacy_policy: "Gizlilik Politikası",
  marketing: "Kampanya ve duyuru e-postaları",
};

const KARARLAR: Record<string, string> = {
  granted: "Onaylandı",
  withdrawn: "Geri çekildi",
};

/** Onayın hangi akıştan geldiği; kullanıcı "bunu ne zaman yaptım?" sorusunu cevaplayabilsin. */
const KAYNAKLAR: Record<string, string> = {
  registration: "Kayıt sırasında",
  reconsent: "Metin güncellendiğinde",
  profile: "Hesap ayarlarından",
};

function ad(sozluk: Record<string, string>, anahtar: string): string {
  return sozluk[anahtar] ?? anahtar;
}

function tarih(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" });
}

export function ConsentsPage() {
  const queryClient = useQueryClient();
  const [hata, setHata] = useState<string | undefined>();

  const sorgu = useQuery({
    queryKey: ["session", "consent-history"],
    queryFn: async () => (await api<ConsentHistoryRow[]>("/api/session/consents")).data,
  });
  const { data: satirlar, isLoading } = sorgu;

  const geriCek = useMutation({
    mutationFn: async (documentId: string) => {
      const govde = withdrawConsentSchema.parse({ documentId });
      await api("/api/session/consents/withdraw", {
        method: "POST",
        body: JSON.stringify(govde),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["session", "consent-history"] });
    },
    onError: (err: unknown) => {
      setHata(err instanceof Error ? err.message : "İzin geri çekilemedi");
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const hepsi = satirlar ?? [];
  // Geri çekilebilecekler: yalnız ZORUNLU OLMAYAN ve şu an onaylı olanlar. Zorunlu bir
  // metin için düğme göstermek, sunucunun kesin olarak reddedeceği bir eylemi teklif
  // etmek olurdu.
  const geriCekilebilir = hepsi.filter((s) => s.current && !s.required && s.decision === "granted");

  const columns: ColumnDef<ConsentHistoryRow>[] = [
    { accessorKey: "type", header: "Metin", cell: ({ row }) => ad(BASLIKLAR, row.original.type) },
    {
      accessorKey: "version",
      header: "Sürüm",
      cell: ({ row }) => `v${row.original.version}`,
    },
    {
      accessorKey: "decision",
      header: "Karar",
      cell: ({ row }) => ad(KARARLAR, row.original.decision),
    },
    {
      accessorKey: "source",
      header: "Kaynak",
      cell: ({ row }) => ad(KAYNAKLAR, row.original.source),
    },
    {
      accessorKey: "grantedAt",
      header: "Tarih",
      cell: ({ row }) => tarih(row.original.grantedAt),
    },
  ];

  return (
    <section className="flex w-full flex-col gap-6">
      <PageHeader
        title="Onaylarım"
        description="Kabul ettiğiniz metinlerin sürümleri ve tarihleri. Geçmiş kayıtlar silinmez."
      />

      {hata ? (
        <p role="alert" className="text-sm text-danger">
          {hata}
        </p>
      ) : null}

      {geriCekilebilir.length > 0 ? (
        <div className="flex flex-col gap-2">
          <h2 className="text-sm font-medium text-fg-strong">İsteğe bağlı izinler</h2>
          {geriCekilebilir.map((s) => (
            <div
              key={s.documentId}
              className="flex items-center justify-between rounded-md border border-border-default px-3 py-2"
            >
              <span className="text-sm text-fg-strong">{ad(BASLIKLAR, s.type)}</span>
              <Button
                variant="secondary"
                disabled={geriCek.isPending}
                onClick={() => {
                  setHata(undefined);
                  geriCek.mutate(s.documentId);
                }}
              >
                İzni geri çek
              </Button>
            </div>
          ))}
        </div>
      ) : null}

      {/*
        ⚠ ARIZA "ONAY VERMEDİM" DEĞİL (kusur avı, 26 Ağustos). Bu ekran kullanıcının hangi
        metni hangi sürümüyle onayladığının kaydı; sorgu düştüğünde "kayıt yok" demek,
        kullanıcıya kendi hukuki geçmişi hakkında yanlış bir şey söylemekti.
      */}
      {sorgu.error ? (
        <StateView
          state={resolveErrorState(sorgu.error, {
            idempotent: true,
            existenceIsSecret: false,
            overrides: {
              error: {
                title: "Onay geçmişiniz yüklenemedi",
                description: "Bağlantı sorunları geçici olabilir.",
              },
            },
          })}
          onRetry={() => void sorgu.refetch()}
        />
      ) : hepsi.length === 0 ? (
        <EmptyState title="Henüz onay kaydınız yok" />
      ) : (
        <Table columns={columns} data={hepsi} />
      )}
    </section>
  );
}
